import React, { useCallback, useState, useEffect, useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  Tabs,
  Tab,
  ListItemText,
  ListItemButton,
  List,
  Checkbox,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  FormControlLabel,
  Switch,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import supabase, { supabaseAnonKey } from "../../../../utils/supabase";
import { useStatusDialog } from "../../../common/StatusDialogProvider";
import ScientificNameSelector from "./parts/ScientificNameSelector";
import SettingsIcon from "@mui/icons-material/Settings";

// Frontend-side detection helpers (backendと同じ基準に寄せる)
const isUndeterminedName = (name, rank) => {
  const n = (name ?? "").toString().toLowerCase();
  return (
    n.includes("sp.") ||
    n.includes("spec.") ||
    n.includes("cf.") ||
    n.includes("indet.") ||
    n.includes("unidentified") ||
    n.includes("incertae sedis") ||
    n.includes("unclassified")
  );
};

const isBoldPlaceholder = (name) => {
  const n = (name ?? "").toString().toLowerCase();
  return (
    n.includes("bold:") || n.includes("bold-") || n.includes("bold_") ||
    n.includes("gbif:") || n.includes("gbif-") || n.includes("gbif_") ||
    n.includes("col:") || n.includes("col-") || n.includes("col_")
  );
};

const isLikelyGbifId = (val) => !!val && /^\d+$/.test(String(val).trim());

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`tabpanel-${index}`} aria-labelledby={`tab-${index}`} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const dataGridStyles = {
  "& .MuiDataGrid-cell": { alignItems: "flex-start", whiteSpace: "normal", lineHeight: 1.3 },
  "& .MuiDataGrid-columnHeaderTitle": { whiteSpace: "normal", lineHeight: 1.2 },
};

const MERGED_COLUMNS = [
  {
    field: "provenance_summary",
    headerName: "Merge Status",
    width: 120,
    renderCell: (params) => {
      const isNew = params.value?.includes("new record");
      return (
        <Chip
          label={isNew ? "NEW" : "MERGE"}
          color={isNew ? "success" : "primary"}
          size="small"
        />
      );
    },
  },
  { field: "scientificName", headerName: "Name", flex: 1, minWidth: 200 },
  { field: "authorship", headerName: "Authority", flex: 1, minWidth: 200 },
  { field: "parentName", headerName: "Parent Name", flex: 1, minWidth: 150 },
  { field: "original_parent_key", headerName: "Original Parent Key", width: 150 },
  { field: "parent_key", headerName: "Current Parent Key", width: 150 },
  { field: "authority_year", headerName: "Year", width: 100 },
  { field: "rank", headerName: "Rank", width: 120 },
  { field: "gbif_id", headerName: "Rep. GBIF ID", width: 140 },
  { field: "taxonomicStatus", headerName: "Status", width: 120 },
  { 
    field: "merged_gbif_ids", 
    headerName: "Merged GBIF IDs", 
    flex: 1, 
    minWidth: 150,
    renderCell: (params) => (
      <Typography variant="body2" sx={{ whiteSpace: 'normal', lineHeight: '1.2' }}>
        {(params.value || []).join(", ")}
      </Typography>
    )
  },
];

const SUSPICIOUS_COLUMNS = [
  { field: "scientificName", headerName: "Name", flex: 1, minWidth: 200 },
  { field: "authorship", headerName: "Authority", flex: 1, minWidth: 200 },
  { field: "original_parent_key", headerName: "Original Parent Key", width: 150 },
  { field: "parent_key", headerName: "Current Parent Key", width: 150 },
  { field: "parentName", headerName: "Parent Name", flex: 1, minWidth: 150 },
  { field: "rank", headerName: "Rank", width: 120 },
  { field: "gbif_id", headerName: "GBIF ID", width: 120 },
  { field: "reason", headerName: "Reason", flex: 1, minWidth: 200 },
];

const EXCLUDED_COLUMNS = [
  { field: "scientificName", headerName: "Name", flex: 1, minWidth: 200 },
  { field: "parentName", headerName: "Parent Name", flex: 1, minWidth: 150 },
  { field: "original_parent_key", headerName: "Original Parent Key", width: 150 },
  { field: "parent_key", headerName: "Current Parent Key", width: 150 },
  { field: "rank", headerName: "Rank", width: 120 },
  { field: "gbif_id", headerName: "GBIF ID", width: 120 },
  { field: "reason", headerName: "Reason", flex: 1, minWidth: 200 },
];

export default function DialogGbifBulkImportV2({ open, onClose }) {
  const { showStatus } = useStatusDialog() || {};

  // Step 1: Parent Selection
  const [parentDbId, setParentDbId] = useState(null);
  const [parentName, setParentName] = useState("");
  
  // Step 2: GBIF Key Search
  const [selectedGbifKeys, setSelectedGbifKeys] = useState(new Set());
  const [gbifSearchLoading, setGbifSearchLoading] = useState(false);
  const [gbifSearchResults, setGbifSearchResults] = useState([]);
  const [gbifSearchError, setGbifSearchError] = useState("");
  const [gbifSearchQuery, setGbifSearchQuery] = useState("");
  const [gbifSearchOpen, setGbifSearchOpen] = useState(false);
  const [autoSearchedQuery, setAutoSearchedQuery] = useState("");
  const [includeUndetermined, setIncludeUndetermined] = useState(false);
  const [includeBold, setIncludeBold] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  
  // Step 3: Review
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [reviewResults, setReviewResults] = useState(null);
  const [mergedRecords, setMergedRecords] = useState([]);
  const [suspiciousRecords, setSuspiciousRecords] = useState([]);
  const [excludedRecords, setExcludedRecords] = useState([]);
  const [currentTab, setCurrentTab] = useState("merged");

  const resetState = () => {
    setParentDbId(null);
    setParentName("");
    setSelectedGbifKeys(new Set());
    setGbifSearchLoading(false);
    setGbifSearchResults([]);
    setGbifSearchError("");
    setGbifSearchQuery("");
    setGbifSearchOpen(false);
    setAutoSearchedQuery("");
    setIncludeUndetermined(false);
    setIncludeBold(false);
    setFilterAnchorEl(null);
    setLoading(false);
    setError("");
    setSuccess("");
    setReviewResults(null);
    setMergedRecords([]);
    setSuspiciousRecords([]);
    setExcludedRecords([]);
    setCurrentTab("merged");
  };

  const handleParentChange = useCallback(async (id) => {
    setParentDbId(id || null);
    setSelectedGbifKeys(new Set());
    setParentName("");
    setReviewResults(null);
    if (!id) return;

    const { data, error: err } = await supabase
      .from("scientific_names")
      .select("id, name_spell_valid, name_spell_original, gbif_id")
      .eq("id", id)
      .maybeSingle();

    if (err) {
      setError(err.message || "親の取得に失敗しました。");
      return;
    }
    
    const pname = data?.name_spell_valid || data?.name_spell_original || "";
    setParentName(pname);
    
    const gbifIdVal = data?.gbif_id;
    if (isLikelyGbifId(gbifIdVal)) {
      setSelectedGbifKeys(new Set([String(gbifIdVal)]));
    } else {
      setGbifSearchQuery(pname);
      setGbifSearchOpen(true);
    }
  }, []);

  const searchGbif = useCallback(async (query) => {
    setGbifSearchLoading(true);
    setGbifSearchError("");
    setGbifSearchResults([]);
    const { data, error: err } = await supabase.functions.invoke("gbif-search", {
      body: { q: query, limit: 20 },
    });
    setGbifSearchLoading(false);

    if (err) {
      setGbifSearchError(err.message || "GBIF検索に失敗しました。");
      return;
    }
    
    const resolved = (typeof data === "string") ? JSON.parse(data) : data;
    if (!Array.isArray(resolved) || resolved.length === 0) {
      setGbifSearchError("GBIF検索で候補が見つかりませんでした。");
      return;
    }
    setGbifSearchResults(resolved);
  }, []);

  // 学名選択直後に自動検索を発火させる
  useEffect(() => {
    if (gbifSearchOpen && gbifSearchQuery && gbifSearchQuery !== autoSearchedQuery && !gbifSearchLoading) {
      setAutoSearchedQuery(gbifSearchQuery);
      searchGbif(gbifSearchQuery);
    }
  }, [gbifSearchOpen, gbifSearchQuery, gbifSearchLoading, autoSearchedQuery, searchGbif]);

  const fetchAndReview = useCallback(async () => {
    if (selectedGbifKeys.size === 0) {
      setError("GBIFの起点となるタクソンが選択されていません。");
      return;
    }
    
    setLoading(true);
    setError("");
    setSuccess("");
    setReviewResults(null);

    const { data, error: err } = await supabase.functions.invoke("gbif-grouped-import", {
      body: { 
        targetGbifKeys: Array.from(selectedGbifKeys), 
        parentId: parentDbId,
        parentCanonical: parentName,
        includeUndetermined,
        includeBold,
      },
    });

    setLoading(false);

    if (err) {
      setError(err.message || "GBIFデータの取得とレビューに失敗しました。");
      showStatus?.({ severity: 'error', title: 'Error', description: err.message });
      return;
    }
    
    setReviewResults(data);
    setMergedRecords(data.merged || []);
    setSuspiciousRecords(data.suspicious || []);
    setExcludedRecords(data.excluded || []);
    setCurrentTab("merged");
    setSuccess(`レビューの準備ができました。名寄せ済み: ${data.merged.length}件, 要確認: ${data.suspicious.length}件`);

  }, [selectedGbifKeys, parentDbId, parentName, includeUndetermined, includeBold, showStatus]);

  const handleRegister = async () => {
    if (!reviewResults?.importId) {
      setError("インポートIDが見つかりません。レビューを再実行してください。");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    const { data, error: err } = await supabase.functions.invoke("gbif-grouped-import", {
      body: { executeImport: true, importId: reviewResults.importId },
    });

    setLoading(false);

    if (err) {
      setError(err.message || "登録処理に失敗しました。");
      showStatus?.({ severity: 'error', title: '登録失敗', description: err.message });
      return;
    }

    setSuccess(`登録が完了しました。新規登録: ${data.inserted_count || 0}件, 更新: ${data.updated_count || 0}件`);
    showStatus?.({ severity: 'success', title: '登録完了', description: `GBIFからの一括登録が完了しました。` });
    onClose?.(true);
  };
  
  useEffect(() => {
    if (open) {
      resetState();
    }
  }, [open]);

  return (
    <>
      <Dialog open={open} onClose={() => onClose?.(false)} maxWidth="xl" fullWidth>
        <DialogTitle>GBIFから一括登録</DialogTitle>
        <DialogContent sx={{ minHeight: 600 }}>
          <Stack spacing={2}>
            {/* Step 1 & 2: Parent and GBIF Key Selection */}
            <Typography variant="h6">Step 1: 起点を選択</Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <ScientificNameSelector
                valueId={parentDbId || ""}
                onChange={handleParentChange}
                inlineAdd
                label="データベース内の親の学名"
                sx={{ flex: 1 }}
              />
              <Box>
                <Typography variant="body2" color="text.secondary">選択中のGBIF Key: {selectedGbifKeys.size}件</Typography>
              </Box>
              <Button variant="outlined" onClick={() => setGbifSearchOpen(true)} disabled={!parentName}>
                GBIF候補を検索・選択
              </Button>
            </Stack>
            
            {/* Step 3: Fetch and review */}
            <Typography variant="h6">Step 2: GBIFからデータを取得してレビュー</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button onClick={fetchAndReview} variant="contained" disabled={selectedGbifKeys.size === 0 || loading}>
                {loading ? <CircularProgress size={24} /> : "取得とレビュー"}
              </Button>
              <Tooltip title="取得条件の設定">
                <IconButton onClick={(e) => setFilterAnchorEl(e.currentTarget)}>
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
              <Stack direction="row" spacing={1}>
                <Chip size="small" label={`未同定: ${includeUndetermined ? "含める" : "除外"}`} />
                <Chip size="small" label={`BOLD: ${includeBold ? "含める" : "除外"}`} />
              </Stack>
            </Stack>

            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">{success}</Alert>}

            {/* Step 4: Display results */}
            {reviewResults && (
              <Box sx={{ width: '100%' }}>
                <Typography variant="h6">Step 3: 内容を確認して登録</Typography>
                <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)} aria-label="import review tabs">
                  <Tab label={`名寄せ済み (${mergedRecords.length})`} value="merged" />
                  <Tab label={`要確認 (${suspiciousRecords.length})`} value="suspicious" />
                  <Tab label={`除外 (${excludedRecords.length})`} value="excluded" />
                </Tabs>
                <TabPanel value={currentTab} index="merged">
                  <Typography variant="body2" color="text.secondary">これらのレコードはデータベースに登録されます。GBIF上の複数の重複が単一のレコードに名寄せされています。</Typography>
                  <Box sx={{ height: 400, width: '100%', mt: 1 }}>
                    <DataGrid
                      density="compact"
                      rows={mergedRecords}
                      columns={MERGED_COLUMNS}
                      getRowId={(r) => r.gbif_id || r.scientificName + r.authorship}
                      getRowHeight={() => 'auto'}
                      sx={dataGridStyles}
                    />
                  </Box>
                </TabPanel>
                <TabPanel value={currentTab} index="suspicious">
                  <Typography variant="body2" color="text.secondary">これらのレコードは、既存データとの関係性が不明瞭などの理由で、今回は登録されません。</Typography>
                  <Box sx={{ height: 400, width: '100%', mt: 1 }}>
                    <DataGrid
                      density="compact"
                      rows={suspiciousRecords}
                      columns={SUSPICIOUS_COLUMNS}
                      getRowId={(r) => r.gbif_id}
                      getRowHeight={() => 'auto'}
                      sx={dataGridStyles}
                    />
                  </Box>
                </TabPanel>
                <TabPanel value={currentTab} index="excluded">
                  <Typography variant="body2" color="text.secondary">未同定種(sp.)などの理由で、処理から除外されたレコードです。</Typography>
                  <Box sx={{ height: 400, width: '100%', mt: 1 }}>
                    <DataGrid
                      density="compact"
                      rows={excludedRecords}
                      columns={EXCLUDED_COLUMNS}
                      getRowId={(r) => r.gbif_id}
                      getRowHeight={() => 'auto'}
                      sx={dataGridStyles}
                    />
                  </Box>
                </TabPanel>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onClose?.(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleRegister} disabled={loading || !reviewResults}>
            登録を実行
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter settings menu */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={() => setFilterAnchorEl(null)}
      >
        <MenuItem disableRipple>
          <FormControlLabel
            control={
              <Switch
                checked={includeUndetermined}
                onChange={(e) => setIncludeUndetermined(e.target.checked)}
              />
            }
            label="未同定を含める"
          />
        </MenuItem>
        <MenuItem disableRipple>
          <FormControlLabel
            control={
              <Switch
                checked={includeBold}
                onChange={(e) => setIncludeBold(e.target.checked)}
              />
            }
            label="BOLDを含める"
          />
        </MenuItem>
        <Box sx={{ px: 2, pb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            設定変更後、再度「取得とレビュー」を実行してください。
          </Typography>
        </Box>
      </Menu>
      
      {/* GBIF Search Dialog */}
      <Dialog open={gbifSearchOpen} onClose={() => setGbifSearchOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>GBIF候補を検索・選択</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="学名で検索"
              value={gbifSearchQuery}
              onChange={(e) => setGbifSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchGbif(gbifSearchQuery)}
              autoFocus
            />
            {gbifSearchError && <Alert severity="error">{gbifSearchError}</Alert>}
            {gbifSearchLoading ? <CircularProgress /> : (
              <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
                {gbifSearchResults.map((r) => {
                  const key = String(r.key);
                  const isSelected = selectedGbifKeys.has(key);
                  return (
                    <ListItemButton key={key} onClick={() => {
                      setSelectedGbifKeys(prev => {
                        const next = new Set(prev);
                        if (next.has(key)) {
                          next.delete(key);
                        } else {
                          next.add(key);
                        }
                        return next;
                      });
                    }}>
                      <Checkbox
                        edge="start"
                        checked={isSelected}
                        tabIndex={-1}
                        disableRipple
                      />
                      <ListItemText 
                        primary={`${r.scientificName} ${r.authorship || ''}`}
                        secondary={`${r.rank}: ${r.kingdom}>${r.phylum}>${r.class}>${r.order}>${r.family}`}
                      />
                      <Chip label={r.taxonomicStatus} color={r.taxonomicStatus === 'ACCEPTED' ? 'success' : 'default'} />
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGbifSearchOpen(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
